'use client'

import { useCallback, useMemo } from 'react'

import type { Locale } from '@/i18n'
import type { ReleaseReviewsData } from '@/lib/supabase/get-release-reviews'
import { tw } from '@/lib/utils'

import ModelFilterSelect from './model-filter-select'
import { fillLabel } from './reviews/fill-label'
import { RatingSummary, type RatingSummaryLabels } from './reviews/rating-summary'
import { ReviewCard } from './reviews/review-card'
import { ReviewComposer, type ReviewComposerLabels } from './reviews/review-composer'
import { useReviews } from './reviews/use-reviews'

interface ModelOption {
  id: number
  name: string
}

export interface ReviewLabels extends RatingSummaryLabels, ReviewComposerLabels {
  seeReviewsFor: string
  allModels: string
  readMoreReviews: string
  loadingReviews: string
  anonymous: string
  showMore: string
  showLess: string
  emptyReviewIntro: string
  noReviewsForModel: string
  showAllModels: string
  /** "Model: {{model}}" */
  ownsModel: string
}

interface CustomerReviewsClientProps {
  releaseId: number
  releaseFullName: string
  initialData: ReleaseReviewsData
  models: ModelOption[]
  locale: Locale
  labels: ReviewLabels
}

const TEXT_ACTION =
  'text-lapis cursor-pointer text-[16px] font-medium underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis'

// Review scores come in half stars: "3 out of 5 stars", "3.5 out of 5 stars".
const formatScore = (score: number) => (Number.isInteger(score) ? String(score) : score.toFixed(1))

export const CustomerReviewsClient = ({
  releaseId,
  releaseFullName,
  initialData,
  models,
  locale,
  labels,
}: CustomerReviewsClientProps) => {
  const {
    reviews,
    totalCount,
    averageScore,
    distribution,
    selectedModelId,
    loading,
    userScore,
    existingReviewId,
    isModalOpen,
    hasMore,
    setModelFilter,
    loadMore,
    setUserScore,
    openModal,
    closeModal,
    onReviewSubmitted,
  } = useReviews({ releaseId, initialData })

  const modelNames = useMemo(() => new Map(models.map((model) => [model.id, model.name])), [models])

  const handleModelChange = useCallback(
    (value: string) => {
      const index = Number(value)
      setModelFilter(index === 0 ? null : models[index - 1].id)
    },
    [models, setModelFilter]
  )

  const hasAnyReviews = initialData.totalCount > 0
  const showFilter = hasAnyReviews && models.length > 1
  const selectedIndex = selectedModelId ? models.findIndex((model) => model.id === selectedModelId) + 1 : 0
  const filteredEmpty = hasAnyReviews && !loading && reviews.length === 0
  const reviewComposer = (
    <ReviewComposer
      variant={hasAnyReviews ? 'compact' : 'inline'}
      releaseId={releaseId}
      releaseName={releaseFullName}
      score={userScore}
      onScore={setUserScore}
      existingReviewId={existingReviewId}
      onSubmitted={onReviewSubmitted}
      isModalOpen={isModalOpen}
      openModal={openModal}
      closeModal={closeModal}
      labels={labels}
    />
  )

  return (
    <div>
      {hasAnyReviews ? (
        <div className="lg:grid lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:grid-rows-[auto_auto_1fr] lg:items-start lg:gap-x-12 lg:gap-y-6">
          <RatingSummary
            className="lg:col-start-1 lg:row-start-1"
            averageScore={averageScore}
            totalCount={totalCount}
            distribution={distribution}
            loading={loading}
            labels={labels}
            filter={
              showFilter && (
                <div className="flex flex-col gap-2">
                  <span className="text-navy-80 text-[16px]">{labels.seeReviewsFor}</span>
                  <ModelFilterSelect
                    className="lg:w-full"
                    ariaLabel={labels.seeReviewsFor}
                    value={String(selectedIndex)}
                    onValueChange={handleModelChange}
                    options={[
                      { value: '0', label: labels.allModels },
                      ...models.map((model, index) => ({ value: String(index + 1), label: model.name })),
                    ]}
                  />
                </div>
              )
            }
          />

          <div className="mt-8 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:mt-0">
            {filteredEmpty ? (
              <div className="py-1">
                <p className="text-navy text-[18px]">{labels.noReviewsForModel}</p>
                <button
                  type="button"
                  data-tracking="ignore"
                  className={tw('mt-2', TEXT_ACTION)}
                  onClick={() => setModelFilter(null)}
                >
                  {labels.showAllModels}
                </button>
              </div>
            ) : (
              <div className={tw('transition-opacity', loading && 'opacity-50')} aria-busy={loading || undefined}>
                {reviews.map((review, index) => {
                  const modelName = review.modelId != null ? modelNames.get(review.modelId) : undefined
                  return (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      locale={locale}
                      anonymousLabel={labels.anonymous}
                      showMoreLabel={labels.showMore}
                      showLessLabel={labels.showLess}
                      starsLabel={fillLabel(labels.starsOutOf5, { score: formatScore(review.score) })}
                      modelNote={
                        models.length > 1 && modelName ? fillLabel(labels.ownsModel, { model: modelName }) : null
                      }
                      className={tw(
                        'border-navy-10 border-b py-8',
                        index === 0 && 'pt-0 lg:pt-1',
                        index === reviews.length - 1 && 'border-b-0 pb-0'
                      )}
                    />
                  )
                })}
                {loading && reviews.length === 0 && <p className="text-navy-80 text-[16px]">{labels.loadingReviews}</p>}
              </div>
            )}

            {hasMore && (
              <button
                type="button"
                data-tracking="ignore"
                disabled={loading}
                onClick={loadMore}
                className="border-lapis text-lapis hover:bg-lapis/5 mt-8 inline-flex min-h-[48px] w-full cursor-pointer items-center justify-center gap-2 rounded-full border px-6 text-[16px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis disabled:cursor-default disabled:opacity-60 sm:w-auto"
              >
                {loading ? labels.loadingReviews : labels.readMoreReviews}
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 6l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="mt-8 lg:col-start-1 lg:row-start-2 lg:mt-0">{reviewComposer}</div>
        </div>
      ) : (
        <div className="max-w-[760px]">
          <p className="mb-5 text-[16px] leading-relaxed text-navy-80">{labels.emptyReviewIntro}</p>
          {reviewComposer}
        </div>
      )}
    </div>
  )
}
